import { useState, useEffect } from "react";

const CONSENT_KEY = "cookie_consent";

export type ConsentChoices = {
  essential: boolean; // Always true, required for app to function
  analytics: boolean; // Firebase Analytics
  timestamp: number;
};

// eslint-disable-next-line react-refresh/only-export-components
export function getConsent(): ConsentChoices | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function hasAnalyticsConsent(): boolean {
  const consent = getConsent();
  return consent?.analytics === true;
}

// eslint-disable-next-line react-refresh/only-export-components
export function saveConsent(choices: ConsentChoices): void {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(choices));
}

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if consent has been given
    const consent = getConsent();
    if (!consent) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      timestamp: Date.now(),
    });
    setShowBanner(false);
    // Reload to initialize analytics
    window.location.reload();
  };

  const handleAcceptEssential = () => {
    saveConsent({
      essential: true,
      analytics: false,
      timestamp: Date.now(),
    });
    setShowBanner(false);
  };

  const handleCustomize = () => {
    setShowDetails(true);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-bg border-t border-border">
      <div className="max-w-[440px] mx-auto">
        {!showDetails ? (
          // Simple banner
          <div>
            <p className="text-sm text-text text-center mb-3">
              We use local storage and analytics cookies to improve your experience.{" "}
              <button
                onClick={handleCustomize}
                className="text-accent hover:underline"
              >
                Customize
              </button>
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleAcceptEssential}
                className="flex-1 px-4 py-2 text-sm border border-border rounded-md font-[family-name:--font-ui] font-medium hover:bg-black/5 transition-colors text-text"
              >
                Essential Only
              </button>
              <button
                onClick={handleAcceptAll}
                className="flex-1 px-4 py-2 text-sm bg-accent text-white rounded-md font-[family-name:--font-ui] font-medium hover:opacity-85 transition-opacity"
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          // Detailed consent
          <div>
            <p className="section-label">Cookie Preferences</p>

            <div className="space-y-3 mb-4">
              {/* Essential cookies - always on */}
              <div className="flex items-start gap-3 py-3 border-b border-border-light">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="mt-1 w-4 h-4 accent-accent"
                />
                <div className="flex-1">
                  <p className="text-sm text-text font-[family-name:--font-ui] font-medium">Essential Cookies</p>
                  <p className="text-xs text-muted font-[family-name:--font-ui]">
                    Required for the app to function. Includes authentication, your consent choice, and preferences stored on your device via local storage.
                  </p>
                </div>
                <span className="text-xs text-muted font-[family-name:--font-ui]">Required</span>
              </div>

              {/* Analytics cookies */}
              <label className="flex items-start gap-3 py-3 cursor-pointer">
                <input
                  type="checkbox"
                  id="analytics-consent"
                  defaultChecked={true}
                  className="mt-1 w-4 h-4 accent-accent"
                />
                <div className="flex-1">
                  <p className="text-sm text-text font-[family-name:--font-ui] font-medium">Analytics Cookies</p>
                  <p className="text-xs text-muted font-[family-name:--font-ui]">
                    Help us understand how you use the app so we can improve it. Uses Google Firebase Analytics to track anonymized feature usage, navigation, and performance. No personal information (name, email) is sent.
                  </p>
                </div>
                <span className="text-xs text-muted font-[family-name:--font-ui]">Optional</span>
              </label>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 py-2 text-sm border border-border rounded-md font-[family-name:--font-ui] font-medium hover:bg-black/5 transition-colors text-text"
              >
                Back
              </button>
              <button
                onClick={() => {
                  const analyticsCheckbox = document.getElementById("analytics-consent") as HTMLInputElement;
                  saveConsent({
                    essential: true,
                    analytics: analyticsCheckbox?.checked ?? false,
                    timestamp: Date.now(),
                  });
                  setShowBanner(false);
                  if (analyticsCheckbox?.checked) {
                    window.location.reload();
                  }
                }}
                className="px-4 py-2 text-sm bg-accent text-white rounded-md font-[family-name:--font-ui] font-medium hover:opacity-85 transition-opacity"
              >
                Save Preferences
              </button>
            </div>

            <p className="text-[11px] text-muted mt-3 font-[family-name:--font-ui]">
              You can change your preferences at any time in your Profile settings.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
